import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../../../core/components/base/base.component';
import {
  REFERRAL_SUMMARY_FILES_MAX,
  ShareableFile,
  describeReferralFailure,
  groupShareableFiles,
  toggleSummaryFile,
} from '../../../../../core/models/referral.model';
import { FeedbackService } from '../../../../../core/services/feedback.service';
import { ReferralService } from '../../../../../core/services/referral.service';

export interface ReferralSummaryFilePickerData {
  patientId: string;
  /** The files already chosen, kept in their order. */
  selected: ShareableFile[];
}

/**
 * Choose files from the patient file to send with a summary referral (tasks/referrals-platform-architecture.md §16, M2).
 *
 * Lists exactly what a full share would send, grouped by timeline entry, newest first: images (as they look now, drawings
 * included) and PDFs (sent with their pages). Files are chosen by reference; the server resolves them again when the
 * referral is created and when it is sent. Closes with the chosen files in the order they were picked, or nothing on cancel.
 */
@Component({
  selector: 'app-referral-summary-file-picker',
  templateUrl: './referral-summary-file-picker.component.html',
  styleUrls: ['./referral-summary-file-picker.component.scss'],
})
export class ReferralSummaryFilePickerComponent extends BaseComponent implements OnInit {
  files: ShareableFile[] = [];
  groups: ReturnType<typeof groupShareableFiles> = [];
  selected: string[] = [];
  max = REFERRAL_SUMMARY_FILES_MAX;
  isLoading = true;
  loadError: string | null = null;

  constructor(
    private referralService: ReferralService,
    private dialogRef: MatDialogRef<ReferralSummaryFilePickerComponent, ShareableFile[]>,
    @Inject(MAT_DIALOG_DATA) public data: ReferralSummaryFilePickerData,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.referralService.listShareableFiles(this.data.patientId).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ files, maxFiles }) => {
        this.files = files;
        this.groups = groupShareableFiles(files);
        this.max = maxFiles || REFERRAL_SUMMARY_FILES_MAX;
        // A file chosen earlier that is no longer offered (deleted since) is dropped from the choice.
        const offered = new Set(files.map((f) => f.fileRef));
        this.selected = (this.data.selected || []).map((f) => f.fileRef).filter((ref) => offered.has(ref));
        this.isLoading = false;
      },
      error: (error) => {
        this.isLoading = false;
        this.loadError = describeReferralFailure(error, "The patient's files could not be loaded.");
      },
    });
  }

  isSelected(file: ShareableFile): boolean {
    return this.selected.includes(file.fileRef);
  }

  /** Where this file is in the order chosen (1-based), for the tile badge. */
  position(file: ShareableFile): number {
    return this.selected.indexOf(file.fileRef) + 1;
  }

  get atLimit(): boolean {
    return this.selected.length >= this.max;
  }

  toggle(file: ShareableFile): void {
    this.selected = toggleSummaryFile(this.selected, file.fileRef, this.max);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  done(): void {
    const byRef = new Map(this.files.map((f) => [f.fileRef, f]));
    this.dialogRef.close(this.selected.map((ref) => byRef.get(ref)).filter((f): f is ShareableFile => !!f));
  }

  trackByKey(_index: number, group: { key: string }): string {
    return group.key;
  }

  trackByRef(_index: number, file: ShareableFile): string {
    return file.fileRef;
  }
}
